import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Button, CertificateCard, Icon, Pill } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { usePermission } from "@/auth/usePermission";
import { SplashScreen } from "@/app/SplashScreen";
import { ClientsList } from "@/components/clients/ClientsList";
import { useActiveWorkspace } from "@/contexts/WorkspaceContext";
import { useClients } from "@/hooks/useClients";
import {
  useDeleteOrganization,
  useOrganization,
  useOrgCertificate,
  useRevokeOrgCertificate,
  useUploadOrgCertificate,
} from "@/hooks/useOrganizations";
import { getInitials } from "@/utils/initials";
import { OrgAddressSection } from "./OrgAddressSection";
import { OrgFormModal } from "./OrgFormModal";
import { OrgMembersSection } from "./OrgMembersSection";
import "./OrganizationDetail.css";

export function OrganizationDetail() {
  const { activeWorkspaceId } = useActiveWorkspace();
  const navigate = useNavigate();
  const orgId = Number(useParams().id);
  const { data: org, isLoading, isError } = useOrganization(activeWorkspaceId, orgId);
  const { data: clients = [] } = useClients(activeWorkspaceId, orgId);
  const del = useDeleteOrganization(activeWorkspaceId ?? 0);
  const [editing, setEditing] = useState(false);

  if (!Number.isFinite(orgId)) return <Navigate to="/organizacoes" replace />;
  if (isLoading) return <SplashScreen />;
  if (isError || !org || activeWorkspaceId == null) return <Navigate to="/organizacoes" replace />;

  const ativas = clients.filter((c) => c.active).length;

  async function onDelete() {
    if (!confirm("Excluir esta organização?")) return;
    try {
      await del.mutateAsync(orgId);
      navigate("/organizacoes");
    } catch {
      alert("Não foi possível excluir — verifique se há empresas ativas vinculadas.");
    }
  }

  return (
    <>
      <button className="detail-back" onClick={() => navigate("/organizacoes")}>
        <Icon name="arrow-left" size={15} />
        Organizações
      </button>

      <div className="detail-hero">
        <span className="org-mono-mark xl">{getInitials(org.nickname ?? org.legal_name)}</span>
        <div className="detail-hero-info">
          <div className="detail-hero-info-head">
            <h1 className="page-title" style={{ margin: 0 }}>
              {org.nickname ?? org.legal_name}
            </h1>
            <Pill tone={org.active ? "ativa" : "pausada"}>{org.active ? "Ativa" : "Inativa"}</Pill>
          </div>
          <p className="page-sub" style={{ marginTop: 4 }}>
            {org.legal_name} · {org.cnpj_masked} · {clients.length}{" "}
            {clients.length === 1 ? "empresa" : "empresas"}
          </p>
          <div className="detail-hero-meta">
            <div className="detail-hero-meta-item">
              <div className="l">CNPJ</div>
              <div className="v">{org.cnpj ?? org.cnpj_masked}</div>
            </div>
            <div className="detail-hero-meta-item">
              <div className="l">E-mail</div>
              <div className="v">{org.email ?? org.email_masked ?? "—"}</div>
            </div>
            <div className="detail-hero-meta-item">
              <div className="l">Empresas ativas</div>
              <div className="v">
                {ativas}/{clients.length}
              </div>
            </div>
            <div className="detail-hero-meta-item">
              <div className="l">Rec. terceiros</div>
              <div className="v">{org.third_party_funds ? "Sim" : "Não"}</div>
            </div>
          </div>
        </div>
        <div className="detail-hero-actions">
          <Guard permission="organizations.write">
            <Button variant="ghost" size="sm" iconLeft="settings" onClick={() => setEditing(true)}>
              Editar
            </Button>
          </Guard>
          <Guard permission="organizations.delete">
            <Button
              variant="danger"
              size="sm"
              iconLeft="x"
              onClick={() => void onDelete()}
              disabled={del.isPending}
            >
              Excluir
            </Button>
          </Guard>
        </div>
      </div>

      <h2 className="td-h4" style={{ margin: "0 0 12px" }}>
        Empresas
      </h2>
      <ClientsList workspaceId={activeWorkspaceId} organizationId={org.id} />

      <div style={{ marginTop: 28 }}>
        <OrgAddressSection workspaceId={activeWorkspaceId} org={org} />
      </div>

      <div style={{ marginTop: 28 }}>
        <OrgCertificateCard workspaceId={activeWorkspaceId} orgId={org.id} />
      </div>

      <Guard permission="access_grants.write">
        <h2 className="td-h4" style={{ margin: "28px 0 12px" }}>
          Membros com acesso
        </h2>
        <OrgMembersSection workspaceId={activeWorkspaceId} organizationId={org.id} />
      </Guard>

      {editing && (
        <OrgFormModal
          mode="edit"
          workspaceId={activeWorkspaceId}
          org={org}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function OrgCertificateCard({ workspaceId, orgId }: { workspaceId: number; orgId: number }) {
  const { data: cert, isLoading } = useOrgCertificate(workspaceId, orgId);
  const upload = useUploadOrgCertificate(workspaceId, orgId);
  const revoke = useRevokeOrgCertificate(workspaceId, orgId);
  const canManage = usePermission("organizations.write");
  return (
    <CertificateCard
      cert={cert}
      isLoading={isLoading}
      canManage={canManage}
      onUpload={(file, password) => upload.mutateAsync({ file, password })}
      onRevoke={() => revoke.mutateAsync()}
    />
  );
}
